import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChakraBox } from '@equip.health/ui';
import { useVirtualizer } from '@tanstack/react-virtual';
import TimeSlotGroup from './TimeSlotGroup';
import TimeSlot from './TimeSlot';
import { isSlotSelected } from './util';

interface DateGroup {
  date: string;
  slots: AppointmentSlot[];
}

interface VirtualizedSlotListProps {
  groupedSlotsByDate: Record<string, AppointmentSlot[]>;
  id: string;
  onSelect: (slot: ProviderAppointmentSlot) => void;
  patientAvailabilityPreferences: PatientAvailabilityPreferences | null;
  patientTimezone?: string;
  providerMap: Record<string, GetProviderByIdResponse>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  selectedSlot?: ProviderAppointmentSlot;
}

const SLOT_ROW_HEIGHT = 76;
const HEADER_HEIGHT = 44;
const GROUP_GAP = 24;
const SLOTS_PER_ROW = 3;

const estimateGroupHeight = (slotCount: number): number => {
  const rows = Math.ceil(slotCount / SLOTS_PER_ROW);
  return HEADER_HEIGHT + rows * SLOT_ROW_HEIGHT + GROUP_GAP;
};

const VirtualizedSlotList = ({
  groupedSlotsByDate,
  id,
  onSelect,
  patientAvailabilityPreferences,
  patientTimezone,
  providerMap,
  scrollContainerRef,
  selectedSlot,
}: VirtualizedSlotListProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const wrapperEl = wrapperRef.current;
    if (!scrollEl || !wrapperEl) return;
    setScrollMargin(
      wrapperEl.getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top +
        scrollEl.scrollTop,
    );
  }, [scrollContainerRef]);

  const sortedGroups = useMemo<DateGroup[]>(
    () =>
      Object.entries(groupedSlotsByDate)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, slots]) => ({ date, slots })),
    [groupedSlotsByDate],
  );

  const virtualizer = useVirtualizer({
    count: sortedGroups.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: (index) =>
      estimateGroupHeight(sortedGroups[index].slots.length),
    overscan: 1,
    scrollMargin,
  });

  return (
    <ChakraBox
      ref={wrapperRef}
      height={`${virtualizer.getTotalSize()}px`}
      position="relative"
      width="100%"
    >
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const { date, slots } = sortedGroups[virtualRow.index];
        return (
          <ChakraBox
            data-index={virtualRow.index}
            key={date}
            ref={virtualizer.measureElement}
            left={0}
            paddingBottom={`${GROUP_GAP}px`}
            position="absolute"
            top={0}
            transform={`translateY(${virtualRow.start - scrollMargin}px)`}
            width="100%"
          >
            <TimeSlotGroup
              date={date}
              firstSlotStartTime={slots[0]?.startTime}
              id={id}
              isFirstGroup={virtualRow.index === 0}
              patientTimezone={patientTimezone}
            >
              {slots.map((slot) => (
                <TimeSlot
                  id={id}
                  isSelected={isSlotSelected(slot, selectedSlot)}
                  key={`${slot.startTime}-${slot.providerId}`}
                  onSelect={onSelect}
                  patientAvailabilityPreferences={patientAvailabilityPreferences}
                  provider={providerMap[slot.providerId]}
                  slot={slot}
                />
              ))}
            </TimeSlotGroup>
          </ChakraBox>
        );
      })}
    </ChakraBox>
  );
};

export default memo(VirtualizedSlotList);
